import { useEffect } from 'react';
import { useSettings } from '@wu/widget-core';

type UseLayoutProps = {
  containerRef: React.RefObject<HTMLDivElement>;
  hasBackground: boolean;
};

export function useLayout({ containerRef, hasBackground }: UseLayoutProps) {
  const { languages, style, embedType } = useSettings();

  useEffect(() => {
    const handleResize = () => {
      const { current: container } = containerRef;

      if (!container) {
        return;
      }

      const items = Array.from(container.children) as HTMLDivElement[];

      // TODO: change parentElement to class
      const widgetWrapper =
        embedType === 'inline'
          ? container.parentElement?.parentElement
          : document.body;

      if (!widgetWrapper) {
        return;
      }

      const widgetWrapperStyle = window.getComputedStyle(widgetWrapper);

      const widgetParentWidth =
        widgetWrapper.offsetWidth -
        parseFloat(widgetWrapperStyle.paddingLeft) -
        parseFloat(widgetWrapperStyle.paddingRight);

      let totalWidth = 0;
      const itemsWidth: number[] = [];

      items.forEach((item) => {
        const style = window.getComputedStyle(item);
        const margin =
          parseFloat(style.marginLeft) + parseFloat(style.marginRight);

        const width = item.offsetWidth + margin;

        itemsWidth.push(width);
        totalWidth += width;
      });

      // If the total width is less than the widget parent width, reset styles
      if (totalWidth <= widgetParentWidth) {
        if (container.style.width) {
          container.style.width = 'auto';
        }

        return;
      }

      // Find the maximum row width
      const safeWidgetParentWidth = Math.max(widgetParentWidth, 1);
      const rows = Math.ceil(totalWidth / safeWidgetParentWidth);

      const itemsPerRow = Math.ceil(items.length / rows);

      const rowsWidths = Array.from({ length: rows }, () => 0);
      let currentRowIndex = 0;

      itemsWidth.forEach((width, index) => {
        if (index === itemsPerRow * (currentRowIndex + 1)) {
          currentRowIndex++;
        }

        rowsWidths[currentRowIndex] += width;
      });

      let maxRowWidth = Math.max(...rowsWidths);

      if (maxRowWidth !== 0) {
        if (maxRowWidth > safeWidgetParentWidth) {
          maxRowWidth -= itemsWidth[itemsPerRow - 1];
        }

        container.style.width = `${maxRowWidth}px`;
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, hasBackground, languages, style]);
}
